# Pure maths of the clef note finder (/:lang/apps/clef-notes).
#
# Staff position <-> pitch, the name of a position ("fourth line", "first
# ledger line below"), which staff a pitch goes on, and the keyboard range each
# staff can show. No DOM. Two runtimes, like capo-core: `require()` in node (the
# route renders the default answer with it, jest tests it) and
# `window.SongtiveClefNotes` in the browser.
#
# It stands on the ChordIQ games core (`apps/chordiq-games/src/core`), which
# already owns the diatonic arithmetic, the clef anchors, the Bravura glyph
# table and the notation speller. One staff model on the site, not two.
#
# Coordinates: a DIATONIC index counts lines and spaces from C0 (C4 = 28). A
# STEP counts them on one staff, from its bottom line: 0 = bottom line,
# 1 = first space, 8 = top line. Treble puts E4 on step 0, bass G2, alto F3
# (the alto clef is the C clef on the third line: middle C is its step 4).

build = (theory) ->

    # Three ledger lines above and below each staff, plus the space beyond
    # the third one is NOT offered: the tool stops on the third ledger line.
    MIN_STEP = -6
    MAX_STEP = 14

    MIDDLE_C = theory.diatonic('C', 4)          # 28

    MODES = ['grand', 'treble', 'bass', 'alto']

    # A query value (`?clef=bass`) or anything else -> one of the three modes.
    normalizeMode = (value) ->
        v = String(value or '').toLowerCase()
        if v in MODES then v else 'grand'

    clefsFor = (mode) ->
        m = normalizeMode(mode)
        if m is 'grand' then ['treble', 'bass'] else [m]

    inRange = (step) -> step? and step >= MIN_STEP and step <= MAX_STEP

    # The note at a staff position. `alter` is -1 / 0 / +1.
    stepToNote = (clef, step, alter = 0) ->
        d = theory.stepToDiatonic(clef, step)
        return null unless d?
        info = theory.fromDiatonic(d)
        {
            clef: clef
            step: step
            diatonic: d
            letter: info.letter
            index: theory.letterIndex(info.letter)
            octave: info.octave
            alter: alter
            midi: theory.toMidi({ diatonic: d, alter: alter })
        }

    # A MIDI pitch spelled the way an on-screen keyboard spells it: a white key
    # is its natural, a black key the sharp of the key below — or the flat of
    # the key above when `preferFlat`.
    spellMidi = (midi, preferFlat = false) ->
        natural = theory.midiToNatural(midi)
        base =
            if natural then { n: natural, alter: 0 }
            else if preferFlat then { n: theory.midiToNatural(midi + 1), alter: -1 }
            else { n: theory.midiToNatural(midi - 1), alter: 1 }
        d = theory.diatonic(base.n.letter, base.n.octave)
        info = theory.fromDiatonic(d)
        {
            diatonic: d
            letter: info.letter
            index: theory.letterIndex(info.letter)
            octave: info.octave
            alter: base.alter
            midi: midi
        }

    # Which staff a spelled note is written on. On the grand staff the split is
    # middle C: everything from C4 up goes on the treble staff, everything below
    # on the bass staff — so middle C itself sits on the ledger line below the
    # treble staff. A single-staff mode always uses its own clef.
    clefFor = (mode, diatonic) ->
        m = normalizeMode(mode)
        return m unless m is 'grand'
        if diatonic >= MIDDLE_C then 'treble' else 'bass'

    # A spelled note placed on the staff of `mode`, or null when that staff
    # cannot show it (beyond the third ledger line).
    placeNote = (mode, spelled) ->
        clef = clefFor(mode, spelled.diatonic)
        step = theory.staffStep(clef, spelled.diatonic)
        return null unless inRange(step)
        stepToNote(clef, step, spelled.alter)

    placeMidi = (mode, midi, preferFlat = false) ->
        placeNote(mode, spellMidi(midi, preferFlat))

    # The same pitch written on the other staff of the grand staff, if it fits
    # there: middle C is one ledger line below the treble staff OR one above
    # the bass staff. Null in a single-staff mode, and when it does not fit.
    alternate = (mode, note) ->
        return null unless normalizeMode(mode) is 'grand' and note
        other = if note.clef is 'treble' then 'bass' else 'treble'
        step = theory.staffStep(other, note.diatonic)
        return null unless inRange(step)
        stepToNote(other, step, note.alter)

    # The i18n suffix naming a staff position: clefnotes_pos_<key>.
    positionKey = (step) ->
        return null unless inRange(step)
        if step >= 0 and step <= 8
            return (if step % 2 is 0 then 'line' + (step / 2 + 1) else 'space' + ((step + 1) / 2))
        return 'space_below' if step is -1
        return 'space_above' if step is 9
        if step < 0
            return (if step % 2 is 0 then 'ledger_below' + (-step / 2) else 'below_ledger' + ((-step - 1) / 2))
        if step % 2 is 0 then 'ledger_above' + ((step - 8) / 2) else 'above_ledger' + ((step - 9) / 2)

    isLine = (step) -> step % 2 is 0

    # MIDI range of the piano under each mode: exactly the pitches its staves
    # can show, so every key has a place and every place has a key. The grand
    # staff runs A1-E6, which covers the C2-C6 a teacher expects.
    keyboardRange = (mode) ->
        clefs = clefsFor(mode)
        lows = (stepToNote(c, MIN_STEP).midi for c in clefs)
        highs = (stepToNote(c, MAX_STEP).midi for c in clefs)
        { from: Math.min.apply(null, lows), to: Math.max.apply(null, highs) }

    steps = -> [MIN_STEP..MAX_STEP]

    # Font size of the middle C label: 6.2 units, smaller for a long localized
    # phrase ("До первой октавы (До4)") so it never runs into the note column.
    markLabelSize = (label) ->
        Math.min(6.2, 74 / (0.58 * Math.max(1, String(label or '').length)))

    # Middle C, the reference every clef is read from. It stays marked in every
    # view: one ledger line below the treble staff, one above the bass staff,
    # the centre line of the alto staff. On the grand staff it is marked where
    # it is written, below the treble staff.
    middleMark = (mode) ->
        clef = clefFor(mode, MIDDLE_C)
        stepToNote(clef, theory.staffStep(clef, MIDDLE_C))

    # ------------------------------------------------------------ geometry
    #
    # One layout for the browser widget and the server-rendered static staff,
    # so the fallback and the live staff put every note in the same place.
    # Units: GAP between two staff lines; one step is half a GAP.
    LAYOUT =
        GAP: 10
        W: 200
        PAD: 42               # 4.2 gaps: three ledger lines and a half step of air
        BETWEEN: 70           # treble bottom line to bass top line on the grand staff
        STAFF_LEFT: 12
        STAFF_RIGHT: 196
        CLEF_X: 15
        MARK_X: 70            # the middle C reference mark
        NOTE_X: 125           # the selected note
        LABEL_X: { line: 168, space: 186 }

    layout = (mode) ->
        clefs = clefsFor(mode)
        staves = {}
        y = LAYOUT.PAD
        for clef in clefs
            staves[clef] = { top: y, bottom: y + 4 * LAYOUT.GAP }
            y += 4 * LAYOUT.GAP + LAYOUT.BETWEEN
        last = staves[clefs[clefs.length - 1]]
        {
            clefs: clefs
            staves: staves
            width: LAYOUT.W
            height: last.bottom + LAYOUT.PAD
            stepY: (clef, step) -> staves[clef].bottom - step * LAYOUT.GAP / 2
        }

    # Latin scientific pitch name, "C4", "F#3", "Bb2". For tests and GA4 only:
    # the page prints names through the locale's notation.
    spn = (note) ->
        return '' unless note
        acc = if note.alter > 0 then '#' else if note.alter < 0 then 'b' else ''
        note.letter + acc + note.octave

    # The printed name in a notation spec, with or without the octave.
    display = (note, spec, withOctave = true) ->
        theory.spell(note.index, note.alter or 0, spec) + (if withOctave then note.octave else '')

    {
        MIN_STEP: MIN_STEP
        MAX_STEP: MAX_STEP
        MIDDLE_C: MIDDLE_C
        MODES: MODES
        normalizeMode: normalizeMode
        clefsFor: clefsFor
        inRange: inRange
        stepToNote: stepToNote
        spellMidi: spellMidi
        clefFor: clefFor
        placeNote: placeNote
        placeMidi: placeMidi
        alternate: alternate
        positionKey: positionKey
        isLine: isLine
        keyboardRange: keyboardRange
        steps: steps
        middleMark: middleMark
        markLabelSize: markLabelSize
        LAYOUT: LAYOUT
        layout: layout
        spn: spn
        display: display
        theory: theory
    }

if typeof module isnt 'undefined' and module.exports
    module.exports = build(require('../../chordiq-games/src/core'))
else if typeof window isnt 'undefined'
    window.SongtiveClefNotes = build(window.ChordIQGames.theory)
